import {
  Accordion,
  AccordionBody,
  AccordionContent,
  AccordionItem,
  AccordionItemHeader,
  AccordionTrigger,
} from '@scholastic/mockingjay';
import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import CheckIcon from '@data/assets/check.svg';
import CollapseLeftIcon from '@data/assets/collapseLeft.svg';
import OverviewReportIcon from '@data/assets/overviewReport.svg';
import {
  getAdminReportsGroupedByCategory,
  isAdminSupportedProduct,
  type AdminReportPath,
} from '@data/config/admin-reports';
import { useApplicationsHierarchy } from '@data/hooks/api/use-applications-hierarchy-api/use-applications-hierarchy-api';
import { cn } from '@data/utils/cn/cn';

/**
 * Levels that are the report root rather than a segment under it, so a link to
 * them is `overview` and not `overview/<level>`.
 */
const LEVELS_WITHOUT_A_SEGMENT: Drilldown[] = [Drilldown.AllOrganizations];

/** One icon per report, so adding a report is an entry rather than a branch. */
const REPORT_ICONS: Record<AdminReportPath, ReactNode> = {
  overview: <OverviewReportIcon aria-hidden className="size-4 shrink-0" />,
};

/**
 * The admin side nav: one collapsible section per application, the one being
 * viewed open by default. Only one is open at a time, so the nav stays shorter
 * than the report beside it.
 */
export interface AdminReportNavProps {
  /**
   * The level the reader is on, which a product change keeps them at — the
   * filters below it travel too, so switching product re-asks the same
   * question of a different report rather than starting over at the top.
   */
  drilldown: Drilldown;
}

/**
 * The product list itself, which the sidebar and the drawer both render —
 * below the laptop breakpoint the nav has no room of its own.
 */
export function AdminReportNavMenu({
  drilldown,
  onNavigate,
}: AdminReportNavProps & {
  /** Called on any link click, the active one included. */
  onNavigate?: () => void;
}) {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  // Deliberately unscoped: the nav lists every application in the hierarchy,
  // so the organization filter does not change what the reader can navigate to.
  const { data: applications } = useApplicationsHierarchy();
  // Carried across a product change; the layout stays mounted, so dropping
  // these would silently reset the figures.
  const preservedSearch = useMemo(() => {
    const next = new URLSearchParams();

    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      next.set('from', from);
      next.set('to', to);
    }

    // Repeated rather than set: both filters take several values.
    searchParams
      .getAll('orgId')
      .forEach((orgId) => next.append('orgId', orgId));
    searchParams
      .getAll('teacherId')
      .forEach((teacherId) => next.append('teacherId', teacherId));
    searchParams
      .getAll('classId')
      .forEach((classId) => next.append('classId', classId));
    searchParams
      .getAll('studentId')
      .forEach((studentId) => next.append('studentId', studentId));

    return next.toString();
  }, [searchParams]);

  const drilldownSuffix = LEVELS_WITHOUT_A_SEGMENT.includes(drilldown)
    ? ''
    : `/${drilldown}`;

  // The hierarchy returns products with no admin report built for them.
  const supported = (applications ?? []).filter((application) =>
    isAdminSupportedProduct(application.applicationCode)
  );

  return (
    <Accordion
      className="m-0 gap-0"
      type="single"
      collapsible
      defaultValue={productId}
    >
      {/* Dropping the component's own box leaves the nav card's edge as the only border. */}
      <AccordionBody className="rounded-none border-0">
        {supported.map((application) => {
          const { applicationCode, applicationLabel } = application;
          const categories = Object.entries(
            getAdminReportsGroupedByCategory(
              applicationCode,
              (application.products ?? []).map((product) => product.productCode)
            )
          );

          return (
            <AccordionItem key={applicationCode} value={applicationCode}>
              {/* A bar on the leading edge marks the product being read. */}
              <AccordionItemHeader
                className={cn(
                  'border-l-4',
                  applicationCode === productId
                    ? 'border-l-neutral-1100'
                    : 'border-l-transparent'
                )}
              >
                <AccordionTrigger>{applicationLabel}</AccordionTrigger>
              </AccordionItemHeader>

              <AccordionContent className="bg-brand-white p-4">
                <div className="flex flex-col gap-4">
                  {categories.map(([category, reports]) => (
                    <div key={category}>
                      <span className="mb-1 block text-xs font-bold leading-normal text-neutral-700">
                        {category}
                      </span>

                      <ul className="flex flex-col gap-1">
                        {reports?.map((report) => (
                          <li key={report.path}>
                            <NavLink
                              to={{
                                pathname: `/data/${applicationCode}/${
                                  report.path
                                }${
                                  report.path === 'overview'
                                    ? drilldownSuffix
                                    : ''
                                }`,
                                search: preservedSearch,
                              }}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                cn(
                                  'flex items-center gap-2 rounded-[2px] p-2',
                                  'text-base transition-colors',
                                  isActive
                                    ? 'bg-neutral-1100 text-brand-white'
                                    : 'text-neutral-1300 hover:bg-neutral-100'
                                )
                              }
                            >
                              {({ isActive }) =>
                                isActive ? (
                                  <>
                                    <CheckIcon
                                      aria-hidden
                                      className="size-4 shrink-0"
                                    />
                                    <span>{report.label}</span>
                                  </>
                                ) : (
                                  <>
                                    {REPORT_ICONS[report.path]}
                                    <span>{report.label}</span>
                                  </>
                                )
                              }
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </AccordionBody>
    </Accordion>
  );
}

/**
 * The admin side nav: the product list in its own card, with a control that
 * narrows it to a rail. Rendered from the laptop breakpoint up; below it the
 * report header opens the same list in a drawer.
 */
export function AdminReportNav({ drilldown }: AdminReportNavProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <nav
      aria-label="Reports"
      className={cn(
        'w-full overflow-hidden rounded-lg bg-brand-white shadow-md',
        'laptop:transition-[width] laptop:duration-300 laptop:ease-in-out',
        collapsed ? 'laptop:w-12' : 'laptop:w-[219px]'
      )}
    >
      <div
        className={cn(
          'flex h-12 min-w-8 items-center gap-2 rounded-t-sm',
          'border-b border-neutral-200 px-4 py-3',
          collapsed ? 'justify-center' : 'justify-end'
        )}
      >
        <button
          type="button"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          onClick={() => setCollapsed((prev) => !prev)}
          className="flex size-6 items-center justify-center rounded-sm p-1 text-neutral-1300 hover:text-neutral-900"
        >
          <CollapseLeftIcon
            aria-hidden
            className={cn('size-4', collapsed && 'rotate-180')}
          />
        </button>
      </div>

      {!collapsed && <AdminReportNavMenu drilldown={drilldown} />}
    </nav>
  );
}
